import React, { useState, useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { Modal, Paper, Fade, IconButton, Box, Button } from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import CameraTypeModal from "./AddCameraTypeModal";
import { URL } from "../config";
import axios from "axios";
import toast from "react-hot-toast";

const CameraTypeList = () => {
  const [dataSource, setDataSource] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editCameraType, setEditCameraType] = useState(null);

  const fetchCameraTypes = async () => {
    try {
      const res = await axios.get(`${URL}/cameratypes`);
      const formattedData = res.data.data.map((item) => ({
        ...item,
        cameraTypeImage: item.cameraTypeImage
          ? `${URL}${item.cameraTypeImage}`
          : "",
      }));
      setDataSource(formattedData);
    } catch (err) {
      toast.error("Failed to fetch camera types");
    }
  };

  useEffect(() => {
    fetchCameraTypes();
  }, []);

  const handleEdit = (row) => {
    setEditCameraType(row);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this camera type?"))
      return;

    await axios.delete(`${URL}/cameratypes/${id}`);
    toast.success("Camera Type deleted successfully!");
    fetchCameraTypes();
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "actions",
        header: "Actions",
        size: 100,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton size="small" onClick={() => handleEdit(row.original)}>
              <EditIcon sx={{ color: "#174253" }} fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() =>
                handleDelete(row.original.CameraTypeId)
              }
            >
              <DeleteIcon sx={{ color: "#d32f2f" }} fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
      { accessorKey: "CameraTypeId", header: "ID", size: 60 },
      {
        accessorKey: "CameraTypeName",
        header: "Camera Type Name",
        size: 150,
      },
      {
        accessorKey: "cameraTypeImage",
        header: "Image",
        size: 80,
        Cell: ({ row }) =>
          row.original.cameraTypeImage ? (
            <img
              src={row.original.cameraTypeImage}
              alt="camera"
              style={{
                width: 60,
                height: 60,
                objectFit: "cover",
                borderRadius: 8,
                border: "1px solid #ccc",
              }}
            />
          ) : (
            <Box
              sx={{
                width: 60,
                height: 60,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
                bgcolor: "#f0f0f0",
              }}
            >
              C
            </Box>
          ),
      },
    ],
    []
  );

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#ffffff",
        marginTop: 2,
        p: { xs: 1, md: 0 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          pr: 2,
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", fontWeight: 700, fontSize: 22, color: "#174253", letterSpacing: 0.5, borderLeft: "5px solid #174253", pl: 1.5 }}
        >
          Camera Type List
        </Box>

        <Button
          sx={{
            borderRadius: 25,
            background: "linear-gradient(90deg, #0b8793, #174253)",
            color: "white",
            "&:hover": { background: "linear-gradient(90deg, #174253, #0b8793)" },
          }}
          onClick={() => {
            setEditCameraType(null);
            setModalVisible(true);
          }}
        >
          Add Camera Type
        </Button>
      </Box>

       <Box sx={{ background: "#fff", borderRadius: "0 0 15px 15px" }}>
                    <MaterialReactTable
                      columns={columns}
                      data={dataSource}
                      enableColumnActions={false}
                      enableDensityToggle={false}
                      enableFullScreenToggle={false}
                      enableColumnFilters={false}
                      enableHiding={false}
                      enableGlobalFilter={false}
                      muiTablePaperProps={{ sx: { boxShadow: "none", background: "#fff", borderRadius: "15px" } }}
                      muiTableContainerProps={{ sx: { backgroundColor: "#fff", width: "100%", overflowX: "auto", borderRadius: "12px" } }}
                      muiTableHeadCellProps={{ sx: { backgroundColor: "#e0e0e0ff", color: "#393e4f", fontWeight: 600, fontSize: "16px", borderBottom: "0.5px solid #e3e5eb", textAlign: "left", height: "63px", paddingTop: "23px" } }}
                      muiTableBodyRowProps={{ sx: { backgroundColor: "#fff", "&:hover": { backgroundColor: "#eff6fe" }, "& td": { fontSize: { xs: "12px", sm: "14px" }, p: "8px", borderBottom: "0.5px solid #e3e5eb", height: "54px" } } }}
                      initialState={{ pagination: { pageIndex: 0, pageSize: 10 } }}
                    />
                  </Box>
      <Modal
        open={modalVisible}
        onClose={() => setModalVisible(false)}
        closeAfterTransition
        BackdropProps={{
          style: { backgroundColor: "rgba(0,0,0,0.6)" },
        }}
      >
        <Fade in={modalVisible}>
          <Paper
            sx={{
              position: "fixed",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              bgcolor: "transparent",
            }}
          >
            <CameraTypeModal
              cameratype={editCameraType}
              onClose={() => {
                setModalVisible(false);
                fetchCameraTypes();
              }}
            />
          </Paper>
        </Fade>
      </Modal>
    </Box>
  );
};

export default CameraTypeList;
